/**
 * Display rules for the map and sensor panels. Levels stay in millimetres
 * until formatWater / formatRain.
 */
import { formatRain, formatWater, waterFromMm } from './units';
import { toMs } from './waterLevelQuery';

export const STALE_MS = 6 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
export const WINDOW_PAST_MS = 12 * HOUR_MS;
export const WINDOW_FUTURE_MS = 4 * HOUR_MS;
const TREND_MS = HOUR_MS;
const TREND_MM = 15;

export const STATUS_THEME = {
  normal: { marker: '#2F7A44', text: '#ffffff', tint: '#EAF3EC' },
  watch: { marker: '#F2C14E', text: '#1B1D1C', tint: '#FBF2DA' },
  flooding: { marker: '#B3261E', text: '#ffffff', tint: '#F8E3E1' },
  unknown: { marker: '#5E6662', text: '#ffffff', tint: '#EEEFEC' },
};

export const STATUS_TEXT = {
  normal: { badge: 'NORMAL', headline: 'No flooding', name: 'Normal' },
  watch: { badge: 'WATCH', headline: 'Water is rising', name: 'Watch' },
  flooding: { badge: 'FLOODING', headline: 'Flooding now', name: 'Flooding' },
  unknown: { badge: 'NO RECENT READING', headline: 'No recent water level', name: 'No recent reading' },
};

export const LEGEND = [
  { id: 'normal', label: 'Normal' },
  { id: 'watch', label: 'Watch' },
  { id: 'flooding', label: 'Flooding' },
  { id: 'unknown', label: 'No recent reading' },
];

/** Fresh reading compared with this sensor's yellow and red heights. */
export function levelStatus(levelMm, yellowMm, redMm, atMs, nowMs = Date.now()) {
  if (levelMm == null || !Number.isFinite(Number(levelMm))) return 'unknown';
  if (atMs == null || nowMs - atMs > STALE_MS) return 'unknown';
  return thresholdStatus(levelMm, yellowMm, redMm);
}

/** Threshold only, for a forecast hour that is not a live reading. */
export function thresholdStatus(levelMm, yellowMm, redMm) {
  if (levelMm == null || !Number.isFinite(Number(levelMm))) return 'unknown';
  if (redMm > 0 && levelMm >= redMm) return 'flooding';
  if (yellowMm > 0 && levelMm >= yellowMm) return 'watch';
  return 'normal';
}

/** City codes embedded in device ids, e.g. floodsonic-dc-canal → Daly City. */
const PLACE_BY_CODE = {
  dc: 'Daly City',
};

/**
 * Place name for a sensor. A stored name wins when it is not just the device
 * id. Otherwise a known code in the id becomes the city, and the id stays
 * available as the second line.
 */
export function sensorPlace(sensor, deviceId) {
  const id = deviceId || sensor?.id || '';
  const stored = sensor?.name && sensor.name !== id ? sensor.name : '';
  const parts = id.split('-').filter(Boolean);
  const code = parts[0] === 'floodsonic' ? (parts[1] || '').toLowerCase() : '';
  const city = PLACE_BY_CODE[code] || '';
  return {
    title: stored || city || id || 'Sensor',
    deviceId: id,
  };
}

export function sensorDisplayName(sensor, deviceId) {
  return sensorPlace(sensor, deviceId).title;
}

export function spokenWater(mm, system) {
  if (mm == null || !Number.isFinite(Number(mm))) return 'no water level reported';
  const value = waterFromMm(Number(mm), system).toFixed(2);
  const unit = system === 'us' ? 'feet' : 'meters';
  return `${value} ${unit}`;
}

/** Full timestamp from the latest sensor reading, as the previous sensor page showed it. */
export function formatLastUpdate(ms) {
  if (ms == null) return 'Last update unavailable';
  const stamp = new Date(ms).toLocaleString([], {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
  return `Last update ${stamp}`;
}

export function formatCoordinates(sensor) {
  if (sensor?.latitude == null || sensor?.longitude == null) return null;
  return `${Number(sensor.latitude).toFixed(6)}, ${Number(sensor.longitude).toFixed(6)}`;
}

/** Date and time of the reading the status is based on: "As of Oct 19, 2026, 9:15 PM". */
export function formatAsOf(ms) {
  if (ms == null) return 'Reading time unavailable';
  const date = new Date(ms);
  const day = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `As of ${day}, ${time}`;
}

export function rainOutlook(rainMm, unitSystem) {
  if (rainMm == null || !Number.isFinite(Number(rainMm))) {
    return 'The rain forecast is not available yet.';
  }
  if (Number(rainMm) <= 0) return 'No rain is expected in the next 6 hours.';
  return `${formatRain(Number(rainMm), unitSystem)} of rain is expected in the next 6 hours.`;
}

export function statusSentence(status, rainMm, unitSystem) {
  const rain = rainOutlook(rainMm, unitSystem);
  if (status === 'unknown') {
    return `This sensor has not reported in the last 6 hours. ${rain}`;
  }
  if (status === 'normal' && rainMm != null && Number(rainMm) <= 0) {
    return 'All clear. No rain is expected in the next 6 hours.';
  }
  if (status === 'normal' && rainMm != null && Number.isFinite(Number(rainMm))) {
    return `All clear. ${formatRain(Number(rainMm), unitSystem)} of rain is expected in the next 6 hours.`;
  }
  if (status === 'normal') return 'All clear. The rain forecast is not available yet.';
  return rain;
}

/**
 * Plain status line for the location dashboard.
 * "Current status: No flooding — 0.77 ft (2.30 ft below the watch level)"
 */
export function dashboardStatusLine({ status, levelMm, yellowMm, redMm, unitSystem }) {
  const headline = `Current status: ${(STATUS_TEXT[status] || STATUS_TEXT.unknown).headline}`;
  if (status === 'unknown' || levelMm == null || !Number.isFinite(Number(levelMm))) return headline;
  const level = formatWater(Number(levelMm), unitSystem);
  let gap = null;
  if (redMm > 0 && yellowMm > 0 && levelMm < yellowMm) {
    gap = `${formatWater(yellowMm - levelMm, unitSystem)} below the watch level`;
  } else if (redMm > 0 && levelMm < redMm) {
    gap = `${formatWater(redMm - levelMm, unitSystem)} below the flood level`;
  } else if (redMm > 0) {
    gap = `${formatWater(levelMm - redMm, unitSystem)} over the flood level`;
  }
  return gap ? `${headline} — ${level} (${gap})` : `${headline} — ${level}`;
}

// alerts_rf is rewritten every 15 minutes; an older doc means the scorer stopped.
export const RF_ALERT_MAX_AGE_MS = 45 * 60 * 1000;

/**
 * Extra banner line from the random forest alert (alerts_rf), or null.
 * While the 6 h hold-down keeps the alert on after the model clears, the
 * line says when the model last expected flooding.
 */
export function floodOutlookLine(rfAlert, nowMs = Date.now()) {
  if (!rfAlert || rfAlert.level !== 'red' || rfAlert.gap) return null;
  const updatedMs = toMs(rfAlert.updatedAt);
  if (updatedMs == null || nowMs - updatedMs > RF_ALERT_MAX_AGE_MS) return null;
  const clearedMs = toMs(rfAlert.ruleClearedAt);
  if (rfAlert.holdDown && clearedMs != null) {
    const time = new Date(clearedMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `Flood warning still on — flooding was likely within 2 hours as of ${time}`;
  }
  return 'Flooding likely within 2 hours';
}

/** "X of room before the watch level", or the flood-level equivalents. */
export function clearancePhrase(levelMm, yellowMm, redMm, unitSystem) {
  if (levelMm == null || !Number.isFinite(Number(levelMm)) || !(redMm > 0)) return null;
  if (yellowMm > 0 && levelMm < yellowMm) {
    return `${formatWater(yellowMm - levelMm, unitSystem)} of room before the watch level`;
  }
  if (levelMm < redMm) {
    return `${formatWater(redMm - levelMm, unitSystem)} below the flood level`;
  }
  return `${formatWater(levelMm - redMm, unitSystem)} over the flood level`;
}

export function summaryGapPhrase(levelMm, yellowMm, redMm, unitSystem) {
  if (levelMm == null || !Number.isFinite(Number(levelMm))) return null;
  if (yellowMm > 0 && levelMm < yellowMm) {
    return `${formatWater(yellowMm - levelMm, unitSystem)} below the watch level`;
  }
  if (redMm > 0 && levelMm < redMm) {
    return `${formatWater(redMm - levelMm, unitSystem)} below the flood level`;
  }
  if (redMm > 0) {
    return `${formatWater(levelMm - redMm, unitSystem)} over the flood level`;
  }
  return null;
}

export function gaugeModel(levelMm, yellowMm, redMm) {
  if (!(redMm > 0) || levelMm == null || !Number.isFinite(Number(levelMm))) return null;
  const fill = Math.max(0, Math.min(100, (levelMm / redMm) * 100));
  const watch = yellowMm > 0 ? Math.max(0, Math.min(100, (yellowMm / redMm) * 100)) : null;
  return { fill, watch };
}

/** rising / falling / steady from about an hour of measured points, or null. */
export function trendWord(points) {
  const real = (points || []).filter((p) => p && p.mm != null && p.t != null);
  if (real.length < 2) return null;
  const last = real[real.length - 1];
  const target = last.t - TREND_MS;
  let ref = null;
  for (const point of real) {
    if (point.t <= target) ref = point;
  }
  if (!ref || last.t - ref.t < 30 * 60 * 1000) return null;
  const delta = last.mm - ref.mm;
  if (delta >= TREND_MM) return 'rising';
  if (delta <= -TREND_MM) return 'falling';
  return 'steady';
}

export function historySummary({ status, levelMm, yellowMm, redMm, points, unitSystem }) {
  const text = STATUS_TEXT[status] || STATUS_TEXT.unknown;
  const level = formatWater(levelMm, unitSystem);
  const trend = level === '—' ? null : trendWord(points);
  const middle = trend ? `${level} and ${trend}` : level;
  const gap = summaryGapPhrase(levelMm, yellowMm, redMm, unitSystem);
  return [text.name, middle, gap].filter(Boolean).join(' · ');
}

/**
 * Next-6-hour rain. Prefer the forecast slots when this view actually loaded
 * them (day and short custom). Otherwise use alerts.rainFwd6h, which is
 * already on the status document and is the same forecast total.
 */
export function nextSixHourRainMm(series, alert) {
  if (series?.range?.showForecast && series.rain) {
    const upcoming = (series.rain.slots || []).filter((slot) => slot.upcoming);
    const complete = upcoming.length > 0 && upcoming.every((slot) => Number.isFinite(slot.mm));
    if (complete) return upcoming.reduce((sum, slot) => sum + slot.mm, 0);
  }
  const forward = alert == null ? NaN : Number(alert.rainFwd6h);
  return Number.isFinite(forward) ? forward : null;
}

/** Last 12 h of measured points and the physics forecast trimmed to 4 h. */
export function sliceWindow(series, nowMs) {
  if (!series || nowMs == null) return null;
  const start = nowMs - WINDOW_PAST_MS;
  const end = nowMs + WINDOW_FUTURE_MS;
  return {
    start,
    end,
    nowMs,
    water: (series.waterBuckets || []).filter((p) => p.t >= start && p.t <= nowMs),
    forecast: (series.forecast || []).filter((p) => p.t <= end),
  };
}

export function forecastAt(points, nowMs, hours) {
  const target = nowMs + hours * HOUR_MS;
  let best = null;
  let bestDist = Infinity;
  for (const point of points || []) {
    if (point?.mm == null || point.t == null) continue;
    const dist = Math.abs(point.t - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = point;
    }
  }
  if (!best || bestDist > 30 * 60 * 1000) return null;
  return best;
}

/**
 * Shaded forecast range, only when the document actually carries one.
 * TODO(backend): canal_forecasts points are { t, waterLevelMm } only.
 * Return a band when each point includes lowerMm and upperMm (millimetres).
 */
export function forecastUncertainty(doc, linePoints) {
  if (!doc || !Array.isArray(doc.points) || !linePoints?.length) return null;
  const byTime = new Map();
  for (const raw of doc.points) {
    const t = toMs(raw?.t);
    const lower = Number(raw?.lowerMm ?? raw?.minMm);
    const upper = Number(raw?.upperMm ?? raw?.maxMm);
    if (t == null || !Number.isFinite(lower) || !Number.isFinite(upper)) continue;
    byTime.set(t, { t, lower, upper });
  }
  const band = [];
  for (const point of linePoints) {
    const row = byTime.get(point.t);
    if (!row) return null;
    band.push(row);
  }
  return band.length >= 2 ? band : null;
}
